import { FormikValues, useFormik } from "formik";
import { FC, useEffect, useState } from "react";
import { InvoiceGeneratorProps } from "../interfaces/invoice-generator-props";
import validationSchema from "./tax-info-validation";
import { Invoice } from "../../interfaces/create-invoice.model";
import { generateInvoice } from "../../services/invoice.service";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { PaymentForm } from "../../enums/payment-form";
import { SaleType } from "../../enums/sale-type";
import { CircleNotch, CaretLeft, CheckCircle } from "phosphor-react";

interface TaxInfoProps {
  data: InvoiceGeneratorProps;
  onNextStep: (data: any) => void;
  onPrevStep: (data: any) => void;
}

const TaxInfo: FC<TaxInfoProps> = ({
  data: initialValues,
  onNextStep,
  onPrevStep,
}) => {
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const MySwal = withReactContent(Swal);
  const [paymentForms, setPaymentForms] = useState<PaymentForm[]>([]);

  useEffect(() => {
    let list: PaymentForm[] = [];
    switch (initialValues.saleType) {
      case SaleType.Efectivo:
        initialValues.paymentForm = PaymentForm.Efectivo;
        list.push(PaymentForm.Efectivo);
        break;
      case SaleType.Transferencia:
        initialValues.paymentForm = PaymentForm.Transferencia;
        list.push(PaymentForm.Transferencia);
        break;
      case SaleType.Cheque:
        initialValues.paymentForm = PaymentForm.Cheque;
        list.push(PaymentForm.Cheque);
        break;
      case SaleType.TarjetaCredito:
      case SaleType.TarjetaDebito:
        initialValues.paymentForm =
          initialValues.saleType === SaleType.TarjetaDebito
            ? PaymentForm.TarjetaDebito
            : PaymentForm.TarjetaCredito;
        list.push(PaymentForm.TarjetaCredito);
        list.push(PaymentForm.TarjetaDebito);
        break;
      default:
        break;
    }
    setPaymentForms(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextStepHandler = async (values: FormikValues) => {
    try {
      setIsGeneratingInvoice(true);

      const invoice: Invoice = {
        codigo: values.code,
        correo: values.email,
        folio: values.ticket,
        formaPago: values.paymentForm,
        regimenFiscal: values.fiscalRegime,
        rfc: values.rfc.toUpperCase(),
        nombre: values.name.toUpperCase(),
        cp: values.postalCode,
        calle: values.street,
        noExterior: values.exteriorNumber,
        noInterior: values.interiorNumber,
        ciudad: values.city,
        colonia: values.settlement,
        municipio: values.municipality,
        estado: values.state,
      };

      const response = await generateInvoice(invoice);

      if (response.data.timbrado) {
        onNextStep({
          ...values,
          invoiceId: response.data.id,
        });
      } else {
        await MySwal.fire({
          html: <label>{response.data.mensajeErrorTimbrado}</label>,
          icon: "error",
          confirmButtonColor: "#84cc16",
        });
      }
      setIsGeneratingInvoice(false);
    } catch (err: any) {
      let message = "Error al generar comprobante";
      if (err.response && err.response.status === 409) {
        message = err.response.data.message;
      }
      await MySwal.fire({
        html: <label>{message}</label>,
        icon: "error",
        confirmButtonColor: "#84cc16",
      });
      setIsGeneratingInvoice(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      fiscalRegime: initialValues.fiscalRegime || "601",
    },
    onSubmit: nextStepHandler,
    validationSchema: validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
  });

  // Clases reutilizables para inputs y selects
  const inputClass = (hasError: boolean) => `
    w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-all duration-200
    placeholder-slate-400 text-slate-700
    ${hasError
      ? "border-red-300 focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-red-50"
      : "border-slate-300 focus:ring-2 focus:ring-lime-200 focus:border-lime-500 bg-white"
    }
  `;

  const labelClass = "block text-xs font-bold text-slate-500 uppercase mb-1 tracking-wide";
  const errorClass = "text-red-500 text-[10px] mt-1 ml-1 font-medium";

  return (
    <form onSubmit={formik.handleSubmit} className="animate-fade-in-up">
      <div className="space-y-6">

        {/* Sección: Datos Generales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Nombre / Razón Social (Ocupa 2 columnas en desktop) */}
          <div className="md:col-span-2">
            <label className={labelClass}>Nombre / Razón Social *</label>
            <input
              type="text"
              placeholder="Nombre completo"
              maxLength={250}
              className={`${inputClass(!!(formik.touched.name && formik.errors.name))} uppercase`}
              {...formik.getFieldProps("name")}
            />
            {formik.touched.name && formik.errors.name && (
              <p className={errorClass}>{formik.errors.name as string}</p>
            )}
          </div>

          {/* Régimen Fiscal */}
          <div className="md:col-span-2">
            <label className={labelClass}>Régimen Fiscal *</label>
            <select
              className={inputClass(!!(formik.touched.fiscalRegime && formik.errors.fiscalRegime))}
              {...formik.getFieldProps("fiscalRegime")}
            >
              <option value="601">(601) General de Ley Personas Morales</option>
              <option value="603">(603) Personas Morales con Fines no Lucrativos</option>
              <option value="605">(605) Sueldos y Salarios e Ingresos Asimilados a Salarios</option>
              <option value="606">(606) Arrendamiento</option>
              <option value="607">(607) Régimen de Enajenación o Adquisición de Bienes</option>
              <option value="608">(608) Demás ingresos</option>
              <option value="609">(609) Consolidación</option>
              <option value="610">(610) Residentes en el Extranjero sin Establecimiento Permanente</option>
              <option value="611">(611) Ingresos por Dividendos (socios y accionistas)</option>
              <option value="612">(612) Personas Físicas con Actividades Empresariales y Profesionales</option>
              <option value="614">(614) Ingresos por intereses</option>
              <option value="615">(615) Régimen de los ingresos por obtención de premios</option>
              <option value="616">(616) Sin obligaciones fiscales</option>
              <option value="620">(620) Sociedades Cooperativas de Producción</option>
              <option value="621">(621) Incorporación Fiscal</option>
              <option value="622">(622) Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</option>
              <option value="623">(623) Opcional para Grupos de Sociedades</option>
              <option value="624">(624) Coordinados</option>
              <option value="625">(625) Plataformas Tecnológicas</option>
              <option value="626">(626) Régimen Simplificado de Confianza</option>
              <option value="628">(628) Hidrocarburos</option>
              <option value="629">(629) Regímenes Fiscales Preferentes</option>
              <option value="630">(630) Enajenación de acciones en bolsa</option>
            </select>
          </div>

          {/* Forma de Pago */}
          <div>
            <label className={labelClass}>Forma de Pago *</label>
            <select
              className={inputClass(!!(formik.touched.paymentForm && formik.errors.paymentForm))}
              {...formik.getFieldProps("paymentForm")}
            >
              {paymentForms.includes(PaymentForm.Efectivo) && <option value="1">(01) Efectivo</option>}
              {paymentForms.includes(PaymentForm.Cheque) && <option value="2">(02) Cheque nominativo</option>}
              {paymentForms.includes(PaymentForm.Transferencia) && <option value="3">(03) Transferencia electrónica</option>}
              {paymentForms.includes(PaymentForm.TarjetaCredito) && <option value="4">(04) Tarjeta de crédito</option>}
              {paymentForms.includes(PaymentForm.TarjetaDebito) && <option value="28">(28) Tarjeta de débito</option>}
            </select>
          </div>

          {/* Código Postal */}
          <div>
            <label className={labelClass}>Código Postal *</label>
            <input
              type="text"
              placeholder="00000"
              maxLength={5}
              className={inputClass(!!(formik.touched.postalCode && formik.errors.postalCode))}
              {...formik.getFieldProps("postalCode")}
            />
            {formik.touched.postalCode && formik.errors.postalCode && (
              <p className={errorClass}>{formik.errors.postalCode as string}</p>
            )}
          </div>

          {/* Email (Ancho completo) */}
          <div className="md:col-span-2">
            <label className={labelClass}>Correo Electrónico *</label>
            <input
              type="email"
              placeholder="cliente@ejemplo.com"
              maxLength={250}
              className={inputClass(!!(formik.touched.email && formik.errors.email))}
              {...formik.getFieldProps("email")}
            />
            {formik.touched.email && formik.errors.email && (
              <p className={errorClass}>{formik.errors.email as string}</p>
            )}
          </div>
        </div>

        {/* Botonera */}
        <div className="flex items-center justify-between pt-6 border-t border-slate-100 mt-6">
          <button
            type="button"
            onClick={() => onPrevStep(formik.values)}
            disabled={isGeneratingInvoice}
            className="flex items-center px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <CaretLeft size={16} weight="bold" className="mr-2" />
            REGRESAR
          </button>

          <button
            type="submit"
            disabled={!formik.isValid || isGeneratingInvoice}
            className={`
                px-6 py-3 rounded-lg font-bold text-white shadow-lg transition-all transform flex items-center
                ${!formik.isValid || isGeneratingInvoice
                ? "bg-slate-300 cursor-not-allowed shadow-none"
                : "bg-[#84cc16] hover:bg-[#65a30d] active:scale-95 shadow-lime-500/30"
              }
            `}
          >
            {isGeneratingInvoice ? (
              <CircleNotch size={20} className="animate-spin mr-2" />
            ) : (
              <CheckCircle size={20} weight="bold" className="mr-2" />
            )}
            {isGeneratingInvoice ? "GENERANDO..." : "FACTURAR"}
          </button>
        </div>

      </div>
    </form>
  );
};

export default TaxInfo;